import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

export type AsideCardProps = {
  title?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  children?: ReactNode
  aside?: ReactNode
  asideTitle?: ReactNode
  mainWidthClassName?: string
  dividerClassName?: string
  asideWidthClassName?: string
  className?: string
  contentClassName?: string
  mainClassName?: string
  asideClassName?: string
}

/**
 * Standard two-column Card layout for Location settings.
 *
 * Matches Location settings layout (see LocationForm):
 * - Left column: full width, becomes 3/4 on xl
 * - Vertical divider: only on xl
 * - Right column: hidden until xl, fixed at 1/4 on xl
 */
export default function AsideCard({
  title,
  description,
  icon = null,
  action = null,
  children,
  aside = null,
  asideTitle = null,
  // Layout tuning (defaults match LocationForm)
  mainWidthClassName = 'w-full xl:w-3/4 min-w-0',
  dividerClassName = 'hidden xl:block w-px bg-border self-stretch min-h-[40px]',
  asideWidthClassName = 'hidden xl:block xl:w-1/4',
  className = '',
  contentClassName = '',
  mainClassName = '',
  asideClassName = '',
}: AsideCardProps) {
  return (
    <Card className={className}>
      {(title || description || action) && (
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              {title && (
                <CardTitle className="inline-flex items-center gap-2 text-xl">
                  {icon}
                  {title}
                </CardTitle>
              )}
              {description && <CardDescription className="mt-1">{description}</CardDescription>}
            </div>
            {action ? <div className="shrink-0">{action}</div> : null}
          </div>
        </CardHeader>
      )}

      <CardContent className={contentClassName}>
        <div className="flex gap-8">
          <div className={cn(mainWidthClassName, mainClassName)}>{children}</div>

          {aside ? <div className={dividerClassName} /> : null}

          {aside ? (
            <aside className={cn(asideWidthClassName, asideClassName)}>
              {asideTitle ? (
                <div className="text-sm text-muted-foreground mb-3">{asideTitle}</div>
              ) : null}
              {aside}
            </aside>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
